import { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Badge, Checkbox, Flex, Switch, Table } from "@mantine/core";
import { useNavigate, useSearchParams } from "react-router";
import styled from "styled-components";
import ScrollBox from "../components/common/Table/ScrollBox";
import { media } from "../theme";
import Container from "../components/common/Container";
import PageHeader from "../components/common/PageHeader";
import CustomDatePicker from "../components/common/Inputs/CustomDatePicker";
import CustomSelect from "../components/common/Inputs/CustomSelect";
import CustomSearchInput from "../components/common/Inputs/CustomSearchInput";
import CustomPagination from "../components/common/CustomPagination";
import SaveIcon from "../components/common/Icons/SaveIcon";
import ShiftConflictErrorModal from "../components/common/Modals/ShiftConflictErrorModal";
import { getAllEmployees } from "../api/employeeApi";
import { getDepartmentSelectData } from "../api/departmentApi";
import { getTagSelectData } from "../api/tagApi";
import {
  AssignmentConflict,
  assignEmployeesToShift,
  getCompanyShiftsOnDate,
  getShiftDefinitionSelectData,
} from "../api/shiftDefinitionApi";
import { getShiftTeamSelectData } from "../api/shiftRotationApi";
import { hasSelected, labelList } from "../utils/employeeFields";
import { createNotification } from "../utils/useNotification";
import { formatDayKey, todayKey, toDayKey } from "../utils/shiftPattern";

/**
 * TOPLU VARDİYA ATAMA (plan 2.24) — dönüşümlü vardiya buradan kurulur:
 * aynı kişiye ardışık tarih aralıklarıyla farklı tanımlar atanır.
 *
 *   Ahmet → V1 Gündüz  01–07 Ağustos
 *   Ahmet → V3 Gece    08–14 Ağustos
 *
 * Ekran eskiden backend'de hiç var olmayan `/weekly-shift` ve
 * `/employee/assignShift` uçlarına bakıyordu; canlı `/shift-assignment`
 * uçlarına taşındı.
 *
 * "Vardiya" kolonu SEÇİLEN BAŞLANGIÇ TARİHİNDEKİ düzeni gösterir, sabit bir
 * özellik değil: atamalar tarih aralıklı olduğu için kişinin vardiyası tarihe
 * göre değişir, ve İK'nın atamadan önce görmesi gereken tam olarak budur.
 *
 * ── İKİ KİP (plan 2.33/2.34) ───────────────────────────────────────────────
 *
 * VARDİYA kipi → kişiye doğrudan bir tanım atanır. Sabit düzen, ya da bir
 *   rotanın ÜSTÜNE binen tek günlük istisna ("Ahmet A ekibinde ama 5 Eylül'de
 *   V1'de"). İstisna girerken ekip atamasını kesmek GEREKMEZ; kademeler
 *   arasında çakışma serbest ve o gün istisna kazanır.
 *
 * EKİP kipi → kişi bir ekibe bağlanır, o günkü vardiyası ekibin rotasından
 *   HESAPLANIR. "Ahmet 1 Ağustos'tan itibaren A Ekibinde" tek kayıttır; her
 *   hafta yeni atama girmek gerekmez.
 *
 * Ekran gövdesi ikisinde de aynı — değişen yalnızca ne seçildiği ve hangi
 * alanın gönderildiği. Ayrı bir ekran açmak aynı personel listesini, aynı
 * süzgeçleri ve aynı çakışma akışını ikinci kez yazdırırdı.
 */
const PAGE_SIZE = 8;

const ShiftAssignment = () => {
  const navigate = useNavigate();
  // Departman → Ekipler sekmesindeki "Üyeleri yönet" buraya kip, ekip ve
  // departman seçili olarak geliyor; İK aynı üç seçimi elle tekrarlamasın.
  const [searchParams] = useSearchParams();
  const presetMode = searchParams.get("mode") === "team" ? "team" : null;
  const presetTeam = searchParams.get("team");
  const presetDepartment = searchParams.get("department");

  const [employees, setEmployees] = useState<any[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [tagOptions, setTagOptions] = useState<any[]>([]);
  const [shiftOptions, setShiftOptions] = useState<any[]>([]);
  /** employeeId → o tarihteki düzen (istisna varsa istisna). */
  const [shiftsOnDate, setShiftsOnDate] = useState<Record<string, any>>({});
  /** employeeId → o tarihteki EKİP satırı. İstisna bunu gölgelemez. */
  const [teamsOnDate, setTeamsOnDate] = useState<Record<string, any>>({});
  /** Ekip kipinde "zaten bir ekibi olanları gizle" — varsayılan açık. */
  const [onlyTeamless, setOnlyTeamless] = useState(true);

  const [teamOptions, setTeamOptions] = useState<any[]>([]);
  /** "definition" = doğrudan vardiya / istisna, "team" = rotadan beslenen ekip. */
  const [mode, setMode] = useState<"definition" | "team">(
    presetMode ?? "definition"
  );
  const [selectedShift, setSelectedShift] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(presetTeam);
  // Mantine 8'de tarih seçicilerin değeri ZATEN "YYYY-MM-DD" string; `Date`
  // tutup çevirmeye çalışmak `date.getFullYear is not a function` veriyordu.
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const [searchedName, setSearchedName] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(
    presetDepartment
  );
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [sort, setSort] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [conflicts, setConflicts] = useState<AssignmentConflict[]>([]);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const startDayKey = toDayKey(startDate);   // biçim savunması

  const loadEmployees = useCallback(async () => {
    try {
      const { data }: any = await getAllEmployees();
      setEmployees(data?.data ?? []);
    } catch {
      setEmployees([]);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
    getDepartmentSelectData()
      .then(({ data }: any) => setDepartmentOptions(data?.data ?? []))
      .catch(() => setDepartmentOptions([]));
    getTagSelectData()
      .then(({ data }: any) => setTagOptions(data?.data ?? []))
      .catch(() => setTagOptions([]));
    getShiftDefinitionSelectData()
      .then((data: any) => setShiftOptions(data?.data ?? []))
      .catch(() => setShiftOptions([]));
  }, [loadEmployees]);

  // Ekip listesi departman süzgecine bağlı: ekip departmansız olamaz ve
  // "Üretim'i süz → ekibe ata" İK'nın doğal akışı. Süzgeç yoksa şirketteki
  // bütün ekipler gelir.
  useEffect(() => {
    getShiftTeamSelectData(selectedDepartment ?? undefined)
      .then((data: any) => setTeamOptions(data?.data ?? []))
      .catch(() => setTeamOptions([]));
  }, [selectedDepartment]);

  // Kip değişince karşı kipin seçimi taşınmamalı: "vardiya seçili" hâlde ekibe
  // geçip kaydetmek, backend'in reddettiği "ikisi birden" isteğini üretirdi.
  useEffect(() => {
    if (mode === "team") setSelectedShift(null);
    else setSelectedTeam(null);
  }, [mode]);

  // "Vardiya" kolonu seçilen başlangıç tarihine bağlı; tarih değişince yeniden
  // okunur. Tarih seçilmediyse bugünün düzeni gösterilir.
  const loadShiftsOnDate = useCallback(async () => {
    const dayKey = startDayKey ?? todayKey();
    try {
      const data: any = await getCompanyShiftsOnDate(dayKey);
      const map: Record<string, any> = {};
      const teams: Record<string, any> = {};
      for (const row of data?.data ?? []) {
        // Bir kişinin O GÜN hem ekip hem istisna satırı olabilir (kademeler
        // arasında çakışma serbest). Sütun İSTİSNAYI göstermeli: doktrin
        // `definition`ın `team`in üstüne bindiği yönünde. Düz atama
        // (`map[id] = row`) hangi satırın son geldiğine bakıyordu, yani
        // istisnası olan kişide ekibi gösterebiliyordu.
        if (row.teamName) teams[row.employeeId] = row;
        const current = map[row.employeeId];
        if (!current || (current.teamName && !row.teamName)) {
          map[row.employeeId] = row;
        }
      }
      setShiftsOnDate(map);
      setTeamsOnDate(teams);
    } catch {
      setShiftsOnDate({});
      setTeamsOnDate({});
    }
  }, [startDayKey]);

  useEffect(() => {
    loadShiftsOnDate();
  }, [loadShiftsOnDate]);

  const filtered = useMemo(() => {
    let rows = [...employees];
    if (searchedName) {
      rows = rows.filter((e: any) =>
        e.name?.toLowerCase().includes(searchedName.toLowerCase())
      );
    }
    if (selectedDepartment) {
      rows = rows.filter((e: any) => hasSelected(e.departments, selectedDepartment));
    }
    if (selectedTag) {
      rows = rows.filter((e: any) => hasSelected(e.tags, selectedTag));
    }
    /*
     * "Seçilen tarihte ekibi olmayanlar" — SABİT BİR ÖZELLİK DEĞİL, o günün
     * kesiti. Üyelik tarih aralıklı: bugün ekibi olmayan birinin gelecek hafta
     * başlayan üyeliği olabilir, bugün ekipte olan birininki ay sonunda
     * bitebilir. Bu yüzden `teamsOnDate` başlangıç tarihine göre okunuyor.
     *
     * Kapatılabilir olması şart: A ekibinden B ekibine TRANSFER normal bir
     * işlem ve süzgeç herkesi gizlerse yapılamaz — kapatınca kişinin mevcut
     * ekibi "vardiyası" sütununda görünür ve çakışma akışı devreye girer.
     */
    if (mode === "team" && onlyTeamless) {
      rows = rows.filter((e: any) => !teamsOnDate[e._id]);
    }
    rows.sort((a: any, b: any) =>
      sort === "desc"
        ? String(b.name).localeCompare(String(a.name), "tr")
        : String(a.name).localeCompare(String(b.name), "tr")
    );
    return rows;
  }, [
    employees,
    searchedName,
    selectedDepartment,
    selectedTag,
    sort,
    mode,
    onlyTeamless,
    teamsOnDate,
  ]);

  /**
   * Seçilen tarihte hiçbir düzeni olmayan personel.
   *
   * `shiftsOnDate` yalnızca O TARİHTE geçerli atamaları taşıyor, yani liste
   * tarihe göre değişiyor — "geçmişte vardiyası vardı" bugünün eksiğini
   * kapatmaz. Süzgeçlerden BAĞIMSIZ (`employees` üzerinden) hesaplanıyor:
   * uyarının amacı "şu an baktığın sayfada eksik var mı" değil, "şirkette
   * ölçütsüz kalan var mı".
   */
  const duzensizler = useMemo(
    () => employees.filter((e: any) => !shiftsOnDate[e._id]),
    [employees, shiftsOnDate]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageIds = pageRows.map((e: any) => e._id);
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  useEffect(() => {
    setPage(1);
  }, [searchedName, selectedDepartment, selectedTag, sort, mode, onlyTeamless]);

  const toggleRow = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // "Tümünü seç" yalnızca GÖRÜNEN sayfaya uygulanır — filtrelenmiş 200 kişiyi
  // görmeden seçmek, atamanın geri alınması zor olduğu için istenmez.
  const togglePage = () =>
    setSelectedIds((prev) =>
      allPageSelected
        ? prev.filter((id) => !pageIds.includes(id))
        : Array.from(new Set([...prev, ...pageIds]))
    );

  const submit = async (closePrevious: boolean) => {
    const target = mode === "team" ? selectedTeam : selectedShift;
    if (!target) {
      createNotification({
        success: false,
        message: mode === "team" ? "Bir ekip seçin." : "Bir vardiya seçin.",
      });
      return;
    }
    if (!startDayKey) {
      createNotification({ success: false, message: "Başlangıç tarihi seçin." });
      return;
    }
    if (selectedIds.length === 0) {
      createNotification({ success: false, message: "En az bir personel seçin." });
      return;
    }

    setSaving(true);
    try {
      const response: any = await assignEmployeesToShift({
        // TAM BİRİ gönderilir; backend ikisi birden gelirse reddediyor ve
        // veritabanı da `oneOf` ile aynı kuralı zorluyor.
        ...(mode === "team"
          ? { shiftTeamId: target }
          : { shiftDefinitionId: target }),
        employeeIds: selectedIds,
        startDate: startDayKey,
        endDate: toDayKey(endDate),
        closePrevious,
      });

      if (response?.success) {
        createNotification({ success: true, message: response.message });
        setSelectedIds([]);
        setConflicts([]);
        await loadShiftsOnDate();
        return;
      }

      if (response?.conflicts?.length) {
        setConflicts(response.conflicts);
        setConflictModalOpen(true);
        return;
      }

      createNotification({
        success: false,
        message: response?.message ?? "Atama yapılamadı.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <PageHeader
        pageTitle="Toplu Vardiya Ata"
        subText={
          mode === "team"
            ? "Seçilen personeli bir EKİBE bağlar; o günkü vardiyası ekibin rotasından hesaplanır."
            : "Seçilen personeli bir çalışma düzenine, belirtilen tarih aralığı için atar."
        }
        isButtoned
        buttonText="Kaydet"
        buttonIcon={<SaveIcon size={24} />}
        isButtonDisabled={saving}
        buttonOnClick={() => submit(false)}
      />

      {/*
        ⚠ 2.35'in en görünür sonucu: departman artık mesai saati taşımıyor,
        yani ÇALIŞMA DÜZENİ OLMAYAN personelin ölçütü de yok — geç/erken
        değerlendirilemiyor ve kapanmamış günü hiç sayılmıyor.
        Eskiden departmanın `workHours`'ı bunu örtüyordu ve eksiklik görünmez
        kalıyordu. Uyarı bu yüzden var: sessiz eksik yerine gözle görülür
        eksik (2.25'in dersi).
      */}
      {duzensizler.length > 0 && (
        <WarningBox>
          <WarningTitle>
            {duzensizler.length} personelin çalışma düzeni yok
          </WarningTitle>
          <WarningText>
            Düzeni olmayan personel için geç/erken hesaplanmaz ve kapanmamış
            günü puantaja yazılmaz. Aşağıdaki listeden seçip bir vardiyaya ya
            da ekibe atayın.
          </WarningText>
          <WarningNames>
            {duzensizler
              .slice(0, 12)
              .map((e: any) => e.name)
              .join(" · ")}
            {duzensizler.length > 12 && ` · +${duzensizler.length - 12} kişi`}
          </WarningNames>
        </WarningBox>
      )}

      <Flex mb={28} gap={20} align="flex-end" wrap="wrap">
        <CustomSelect
          label="Atama Türü"
          w={200}
          data={[
            { value: "definition", label: "Vardiya (sabit / istisna)" },
            { value: "team", label: "Ekip (rotadan)" },
          ]}
          value={mode}
          onChange={(v: string | null) =>
            setMode((v as "definition" | "team") ?? "definition")
          }
        />
        {mode === "team" ? (
          <CustomSelect
            label="Ekip"
            placeholder={teamOptions.length ? "Ekip Seçiniz" : "Ekip yok"}
            w={240}
            data={teamOptions}
            value={selectedTeam}
            onChange={setSelectedTeam}
            searchable
            withAsterisk
            disabled={teamOptions.length === 0}
          />
        ) : (
          <CustomSelect
            label="Vardiya"
            placeholder="Vardiya Seçiniz"
            w={240}
            data={shiftOptions}
            value={selectedShift}
            onChange={setSelectedShift}
            searchable
            withAsterisk
          />
        )}
        <CustomDatePicker
          label="Başlangıç Tarihi"
          placeholder="Tarih Seçiniz"
          value={startDate}
          onChange={setStartDate}
          withAsterisk
        />
        <CustomDatePicker
          label="Bitiş Tarihi (Opsiyonel)"
          placeholder="Süresiz"
          value={endDate}
          onChange={setEndDate}
          minDate={startDate ?? undefined}
        />
        {mode === "team" && (
          <Switch
            mb={10}
            color="#5C26FF"
            checked={onlyTeamless}
            onChange={(e) => setOnlyTeamless(e.currentTarget.checked)}
            label="Yalnızca o tarihte ekibi olmayanlar"
          />
        )}
        {selectedIds.length > 0 && (
          <SelectionHint>{selectedIds.length} personel seçili</SelectionHint>
        )}
      </Flex>

      <TableWrapper>
        <FilterRow>
          <CustomSearchInput
            value={searchedName}
            w={220}
            placeholder="Ad Soyad"
            onChange={(event: any) => setSearchedName(event.currentTarget.value)}
          />
          <Flex gap={8}>
            <CustomSelect
              placeholder="Departman"
              data={departmentOptions}
              value={selectedDepartment}
              onChange={setSelectedDepartment}
              clearable
              searchable
            />
            <CustomSelect
              placeholder="Rol / Yetenek"
              data={tagOptions}
              value={selectedTag}
              onChange={setSelectedTag}
              clearable
              searchable
            />
          </Flex>
        </FilterRow>

        <Table style={{ borderCollapse: "separate" }} highlightOnHover verticalSpacing="sm">
          <Table.Thead style={{ background: "#EEF2FF" }}>
            <Table.Tr>
              <StyledTh style={{ width: "1%" }}>
                <Checkbox
                  checked={allPageSelected}
                  onChange={togglePage}
                  color="#5c26ff"
                  size="16px"
                />
              </StyledTh>
              <StyledTh style={{ width: "26%" }}>
                <SortHeader
                  onClick={() => setSort((p) => (p === "desc" ? "asc" : "desc"))}
                >
                  <div>Ad Soyad</div>
                  <Avatar
                    src={
                      sort === "desc"
                        ? "/assets/ascSortIcon.png"
                        : "/assets/descSortIcon.png"
                    }
                    size={16}
                  />
                </SortHeader>
              </StyledTh>
              <StyledTh style={{ width: "16%" }}>Telefon</StyledTh>
              <StyledTh style={{ width: "18%" }}>Departman</StyledTh>
              <StyledTh style={{ width: "16%" }}>Rol / Yetenek</StyledTh>
              <StyledTh style={{ width: "23%" }}>
                {startDayKey
                  ? `${formatDayKey(startDayKey)} vardiyası`
                  : "Bugünkü vardiyası"}
              </StyledTh>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {pageRows.map((employee: any) => {
              const current = shiftsOnDate[employee._id];
              return (
                <StyledTr
                  key={employee._id}
                  onClick={() =>
                    navigate(`/employees/${employee._id}`, {
                      state: window.location.pathname,
                    })
                  }
                >
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.includes(employee._id)}
                      onChange={() => toggleRow(employee._id)}
                      color="#5C26FF"
                      size="16px"
                    />
                  </Table.Td>
                  <Table.Td>
                    <Flex align="center" gap={16}>
                      <Badge circle bg="#EEF2FF" size="40px" style={{ minWidth: 40 }}>
                        <AvatarText>{employee?.name?.[0]}</AvatarText>
                      </Badge>
                      {employee?.name}
                    </Flex>
                  </Table.Td>
                  <Table.Td>{employee?.phone}</Table.Td>
                  <Table.Td>{labelList(employee?.departments)}</Table.Td>
                  <Table.Td>{labelList(employee?.tags)}</Table.Td>
                  <Table.Td>
                    {/*
                      Üç ayrı durum var ve üçü de ayırt edilebilmeli:
                        ekipte + o gün vardiyası var → "A Ekibi · Gece"
                        ekipte + dinlenme/rota yok   → "A Ekibi · dinlenme"
                        hiç ataması yok              → "Vardiyası yok"
                      Ekip satırında vardiya adı backend'de rotadan çözülüyor;
                      panel burada hesap yapmıyor.
                    */}
                    {current?.teamCode ? (
                      <Flex align="center" gap={6} wrap="wrap">
                        <Badge variant="light" color="grape">
                          {current.teamCode} · {current.teamName}
                        </Badge>
                        {current.code ? (
                          <Badge variant="light" color="indigo">
                            {current.code} · {current.name}
                          </Badge>
                        ) : (
                          <NoShift>dinlenme</NoShift>
                        )}
                      </Flex>
                    ) : current?.code ? (
                      <Badge variant="light" color="indigo">
                        {current.code} · {current.name}
                      </Badge>
                    ) : (
                      <NoShift>Vardiyası yok</NoShift>
                    )}
                  </Table.Td>
                </StyledTr>
              );
            })}

            {/*
              Süzgeç hiçbir personel döndürmediğinde tablo gövdesi tamamen boş
              kalıyordu: başlık satırı duruyor, altı bomboş. Kullanıcı "yüklenmedi
              mi, kimse mi yok?" ayrımını yapamıyordu.
            */}
            {pageRows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <EmptyRow>
                    Bu süzgeçlerle personel bulunamadı. Departman, rol/yetenek veya
                    arama kutusunu değiştirin.
                  </EmptyRow>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </TableWrapper>

      {totalPages > 1 && (
        <PaginationWrapper>
          <CustomPagination total={totalPages} value={page} onChange={setPage} />
        </PaginationWrapper>
      )}

      <ShiftConflictErrorModal
        opened={conflictModalOpen}
        setOpened={setConflictModalOpen}
        conflicts={conflicts}
        onCloseAndContinue={() => submit(true)}
      />
    </Container>
  );
};

export default ShiftAssignment;

/* DataTable'daki FilterWrapper ile aynı kural: sabit genişlikli arama kutusu ve
   iki select dar ekranda yan yana sığmıyor. */
const EmptyRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 16px;
  color: #6b7280;
  font-size: 15px;
  text-align: center;
`;

const FilterRow = styled.div`
  display: flex;
  padding: 12px 16px;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;

  ${media.mobile} {
    flex-direction: column;
    align-items: stretch;

    > *,
    > * > * {
      width: 100% !important;
    }
  }
`;

const TableWrapper = styled(ScrollBox)`
  overflow-y: auto;
  min-height: max-content;
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const SelectionHint = styled.div`
  color: #5c26ff;
  font-size: 14px;
  font-weight: 500;
  padding-bottom: 8px;
`;

const SortHeader = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  cursor: pointer;
`;

const NoShift = styled.span`
  color: #9ca3af;
`;

const StyledTh = styled(Table.Td)`
  color: #4c4c4c;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
`;

const StyledTr = styled(Table.Tr)`
  cursor: pointer;
  color: #111827;
  font-size: 16px;
  font-weight: 400;
  &:hover {
    background: #f9fafb !important;
  }
`;

const AvatarText = styled.div`
  color: #21005d;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
`;

const WarningBox = styled.div`
  border: 1px solid #fde68a;
  background: #fffbeb;
  border-radius: 8px;
  padding: 16px 18px;
  margin-bottom: 24px;
`;

const WarningTitle = styled.div`
  color: #92400e;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
`;

const WarningText = styled.div`
  color: #78350f;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 8px;
  max-width: 720px;
`;

const WarningNames = styled.div`
  color: #92400e;
  font-size: 13px;
`;
